import { useEffect, useState } from "react";
import {
  fetchCognitoryQuestionAnswerTraceEvaluation,
  submitCognitoryQuestionAnswerTraceEvaluation,
} from "../api/cognitoryQuestionAnswerEvaluationClient";
import {
  COGNITORY_ACTIVITY_EVALUATION_RATINGS,
  cognitoryActivityEvaluationRatingChoiceStyle,
  cognitoryActivityEvaluationStatusDotColor,
  formatCognitoryActivityEvaluationStatusLabel,
  type CognitoryActivityEvaluationRating,
} from "../presentation/cognitoryActivityEvaluationStatus";
import {
  bindMainPageEvaluationPrimaryButtonInteractions,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_EVALUATION_PRIMARY_BUTTON_STYLE,
  MAIN_PAGE_INPUT_STYLE,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RECORD_CARD_STYLE,
  MAIN_PAGE_SECTION_LABEL_STYLE,
} from "../theme/mainPageVisual";

type Props = {
  cognitoryId: string;
  traceRequestId: string;
  collectionEnabled: boolean;
  featureAvailable: boolean;
};

export default function CognitoryQuestionAnswerEvaluationCard(props: Props) {
  const [rating, setRating] = useState<CognitoryActivityEvaluationRating | "">(
    "",
  );
  const [comment, setComment] = useState("");
  const [saveConfirmed, setSaveConfirmed] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "loading" | "ready" | "saving" | "unavailable"
  >("idle");

  useEffect(() => {
    if (!props.featureAvailable || !props.collectionEnabled) {
      setStatus("unavailable");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    void fetchCognitoryQuestionAnswerTraceEvaluation({
      cognitoryId: props.cognitoryId,
      traceRequestId: props.traceRequestId,
    }).then(result => {
      if (cancelled) {
        return;
      }
      if (result.kind === "found") {
        setRating(result.evaluation.rating);
        setComment(result.evaluation.comment ?? "");
      }
      setStatus("ready");
    });
    return () => {
      cancelled = true;
    };
  }, [
    props.cognitoryId,
    props.traceRequestId,
    props.collectionEnabled,
    props.featureAvailable,
  ]);

  if (status === "unavailable") {
    return null;
  }

  const saveDisabled =
    status === "loading" ||
    status === "saving" ||
    rating === "" ||
    !props.collectionEnabled;

  return (
    <section
      data-testid="cognitory-question-answer-evaluation-card"
      style={{
        ...MAIN_PAGE_RECORD_CARD_STYLE,
        marginTop: "0.65rem",
        padding: "0.75rem 0.85rem",
      }}
    >
      <div style={{ ...MAIN_PAGE_SECTION_LABEL_STYLE, marginBottom: "0.5rem" }}>
        Evaluate this response
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem" }}>
        {COGNITORY_ACTIVITY_EVALUATION_RATINGS.map(option => (
          <button
            key={option}
            type="button"
            data-testid={`cognitory-evaluation-rating-${option}`}
            aria-pressed={rating === option}
            style={{
              ...MAIN_PAGE_QUIET_BUTTON_STYLE,
              marginTop: 0,
              fontWeight: rating === option ? 600 : 400,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              ...cognitoryActivityEvaluationRatingChoiceStyle({
                rating: option,
                selected: rating === option,
              }),
            }}
            disabled={status === "loading" || status === "saving"}
            onClick={() => {
              setSaveConfirmed(false);
              setRating(option);
            }}
          >
            <span
              data-testid={`cognitory-evaluation-rating-dot-${option}`}
              aria-hidden="true"
              style={{
                width: "0.55rem",
                height: "0.55rem",
                borderRadius: "999px",
                background: cognitoryActivityEvaluationStatusDotColor(option),
                flexShrink: 0,
              }}
            />
            {formatCognitoryActivityEvaluationStatusLabel(option)}
          </button>
        ))}
      </div>
      <label style={{ display: "block", marginTop: "0.65rem" }}>
        <span
          style={{
            display: "block",
            fontSize: "0.78rem",
            color: MAIN_PAGE_COLORS.quietInk,
            marginBottom: "0.35rem",
          }}
        >
          Optional comment
        </span>
        <textarea
          data-testid="cognitory-evaluation-comment"
          value={comment}
          onChange={event => {
            setSaveConfirmed(false);
            setComment(event.target.value);
          }}
          rows={3}
          style={{
            ...MAIN_PAGE_INPUT_STYLE,
            width: "100%",
            resize: "vertical",
            fontFamily: "inherit",
          }}
          disabled={status === "loading" || status === "saving"}
        />
      </label>
      <button
        type="button"
        data-testid="cognitory-evaluation-save"
        style={MAIN_PAGE_EVALUATION_PRIMARY_BUTTON_STYLE}
        disabled={saveDisabled}
        onFocus={event => {
          bindMainPageEvaluationPrimaryButtonInteractions(
            event.currentTarget,
            "focus",
          );
        }}
        onBlur={event => {
          bindMainPageEvaluationPrimaryButtonInteractions(
            event.currentTarget,
            "blur",
          );
        }}
        onMouseEnter={event => {
          if (!event.currentTarget.disabled) {
            bindMainPageEvaluationPrimaryButtonInteractions(
              event.currentTarget,
              "enter",
            );
          }
        }}
        onMouseLeave={event => {
          if (!event.currentTarget.disabled) {
            bindMainPageEvaluationPrimaryButtonInteractions(
              event.currentTarget,
              "leave",
            );
          } else {
            bindMainPageEvaluationPrimaryButtonInteractions(
              event.currentTarget,
              "disabled",
            );
          }
        }}
        onClick={() => {
          if (rating === "") {
            return;
          }
          setSaveConfirmed(false);
          setStatus("saving");
          const trimmedComment = comment.trim();
          void submitCognitoryQuestionAnswerTraceEvaluation({
            cognitoryId: props.cognitoryId,
            traceRequestId: props.traceRequestId,
            rating,
            ...(trimmedComment ? { comment: trimmedComment } : {}),
          }).then(result => {
            if (result.kind === "saved") {
              setSaveConfirmed(true);
            }
            setStatus("ready");
          });
        }}
      >
        Save evaluation
      </button>
      {saveConfirmed ? (
        <p
          role="status"
          aria-live="polite"
          data-testid="cognitory-evaluation-save-success"
          style={{
            margin: "0.5rem 0 0",
            fontSize: "0.85rem",
            color: MAIN_PAGE_COLORS.quietInk,
          }}
        >
          Evaluation saved
        </p>
      ) : null}
    </section>
  );
}
